import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { QButton, QCard, QCardContent, QTypography } from '@kw/quokka-ui';
import dayjs from 'dayjs';

interface OrganizationSettingCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  valueLabel: string;
  value: ReactNode;
  lastUpdated?: string | Date;
  onEdit?: () => void;
}

const formatLastUpdated = (lastUpdated?: string | Date) => {
  if (!lastUpdated) return null;

  const parsedDate = dayjs(lastUpdated);
  return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY') : null;
};

export const OrganizationSettingCard = ({
  title,
  description,
  icon,
  valueLabel,
  value,
  lastUpdated,
  onEdit,
}: OrganizationSettingCardProps) => {
  const formattedLastUpdated = formatLastUpdated(lastUpdated);

  return (
    <StyledCard variant="outlined">
      <StyledCardContent>
        <IconTile aria-hidden="true">{icon}</IconTile>
        <CardBody>
          <CardHeader>
            <div>
              <CardTitle variant="h5Medium" component="h2">
                {title}
              </CardTitle>
              <CardDescription variant="bodyRegular">{description}</CardDescription>
            </div>
            {onEdit && (
              <QButton variant="outlined" onClick={onEdit}>
                Edit
              </QButton>
            )}
          </CardHeader>

          <SettingSummary>
            <div>
              <SummaryLabel>{valueLabel}</SummaryLabel>
              <SummaryValue>{value}</SummaryValue>
            </div>
            {formattedLastUpdated && (
              <LastUpdated>
                <SummaryLabel>Last updated</SummaryLabel>
                <LastUpdatedValue>{formattedLastUpdated}</LastUpdatedValue>
              </LastUpdated>
            )}
          </SettingSummary>
        </CardBody>
      </StyledCardContent>
    </StyledCard>
  );
};

const StyledCard = styled(QCard)`
  width: 100%;
  padding: 0;
  background: #ffffff;
`;

const StyledCardContent = styled(QCardContent)`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 24px;

  &:last-child {
    padding-bottom: 24px;
  }
`;

const IconTile = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 8px;
  background: #f3f5f5;

  svg {
    width: 21px;
    height: 21px;
  }
`;

const CardBody = styled('div')`
  flex: 1;
  min-width: 0;
`;

const CardHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  > button {
    flex-shrink: 0;
  }
`;

const CardTitle = styled(QTypography)`
  display: block;
  margin: 0;
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
`;

const CardDescription = styled(QTypography)`
  display: block;
  max-width: 700px;
  margin-top: 6px;
  color: #57666b;
  font-size: 14px;
  line-height: 21px;
`;

const SettingSummary = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const SummaryLabel = styled('div')`
  color: #57666b;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryValue = styled('div')`
  margin-top: 7px;
  font-size: 15px;
  line-height: 21px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const LastUpdated = styled('div')`
  color: #57666b;
  flex-shrink: 0;
  text-align: right;
`;

const LastUpdatedValue = styled('div')`
  margin-top: 7px;
  font-size: 13px;
  line-height: 18px;
`;
